import { ButtonV2, Select, TextInput } from '@medeloop/medeloop-components-library';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import parsePhoneNumber from 'libphonenumber-js';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { selectAccount, selectAuthToken } from 'src/store/selectors';
import { useMutation } from '@tanstack/react-query';
import { updateUser, UpdateUserDTO } from 'src/api/users';
import { safeRedirect } from 'src/utils/safe-redirect';

const COUNTRY_CODES = [
  { logo: '🇺🇸 / 🇨🇦', value: '+1' },
  { logo: '🇬🇧', value: '+44' },
  { logo: '🇲🇽', value: '+52' },
  { logo: '🇧🇷', value: '+55' },
  { logo: '🇨🇴', value: '+57' },
] as const;

const createValidationSchema = (isSSO: boolean) => {
  const baseSchema = {
    givenName: Yup.string().required('').min(2, '').max(50, ''),
    familyName: Yup.string().required('').min(2, '').max(50, ''),
  };

  if (!isSSO) {
    return Yup.object().shape({
      ...baseSchema,
      phoneNumber: Yup.string()
        .required('')
        .matches(/^[0-9]{1,15}$/, 'errors.enter_valid_phone_number'),
      countryCode: Yup.string().required(''),
    });
  }

  return Yup.object().shape(baseSchema);
};

const isUserComplete = (
  user: { givenName?: string; familyName?: string; phoneNumber?: string },
  isSSO: boolean,
): boolean => {
  if (!user.givenName || !user.familyName) {
    return false;
  }
  return isSSO ? true : !!user.phoneNumber;
};

const getRedirectUrl = (location: ReturnType<typeof useLocation>): string | null => {
  const urlFromQuery = new URLSearchParams(location.search).get('redirect_url');
  return urlFromQuery || localStorage.getItem('redirect_url');
};

export function CompleteAccountCreationElement() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = useSelector(selectAuthToken);
  const account = useSelector(selectAccount);

  const [error, setError] = useState('');
  const isSSO = account.isSSO;

  const updateUserMutation = useMutation({
    mutationFn: ({ updateData }: { updateData: UpdateUserDTO; fullPhoneNumber: string }) => {
      return updateUser({ token, updatedUser: updateData });
    },
    onSuccess: (data, variables) => {
      if (!data) {
        setError(t('something_went_wrong'));
        return;
      }

      if (isSSO) {
        const redirectUrl = getRedirectUrl(location);
        if (redirectUrl) {
          safeRedirect(redirectUrl);
        }
        return;
      }

      if (!data.phoneNumber) {
        navigate(`verify-phone${location.search}`, {
          replace: true,
          state: { destination: variables.fullPhoneNumber },
        });
      }
    },
    onError: () => {
      setError(t('something_went_wrong'));
    },
  });

  const { values, handleChange, submitForm, errors, setFieldValue } = useFormik({
    initialValues: {
      givenName: '',
      familyName: '',
      phoneNumber: '',
      countryCode: '+1',
    },
    validationSchema: createValidationSchema(isSSO),
    enableReinitialize: true,
    onSubmit: (formValues) => {
      const fullPhoneNumber = isSSO ? '' : formValues.countryCode + formValues.phoneNumber;
      const updateData: UpdateUserDTO = {
        userId: account.userId,
        givenName: formValues.givenName,
        familyName: formValues.familyName,
        email: account.email,
        phoneNumber: fullPhoneNumber,
      };

      updateUserMutation.mutate({ updateData, fullPhoneNumber });
    },
  });

  useEffect(() => {
    if (!account.userId) return;

    setFieldValue('givenName', account.givenName || '');
    setFieldValue('familyName', account.familyName || '');

    if (!isSSO && account.phoneNumber) {
      setFieldValue('phoneNumber', account.phoneNumber);
      const parsed = parsePhoneNumber(account.phoneNumber);
      setFieldValue('countryCode', parsed?.countryCallingCode || '+1');
    }
  }, [account, isSSO, setFieldValue]);

  // Redirect if user is already complete
  useEffect(() => {
    if (account.userId && isUserComplete(account, isSSO)) {
      navigate(`/sign-in${location.search}`, { replace: true });
    }
  }, [account, isSSO, navigate, location.search]);

  if (!account.userId) {
    return (
      <div className="absolute bg-gray-950 w-full h-full opacity-50 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-6 w-[456px] p-6 text-white relative">
      {updateUserMutation.isPending && (
        <div className="absolute bg-dark-subdued-background w-full h-full opacity-50" />
      )}
      <div className="flex flex-col gap-2 items-center">
        <h1 className="text-white text-4xl font-semibold text-center">
          {t('create_your_account')}
        </h1>
        <span className="text-gray-400 text-center">{t('complete_your_account_details')}</span>
      </div>

      <div className="flex flex-col w-full">
        <TextInput
          label={t('given_name')}
          name="givenName"
          type="text"
          placeHolder={t('enter_given_name')}
          value={values.givenName}
          onChange={handleChange}
          autoFocus
        />

        <TextInput
          label={t('family_name')}
          name="familyName"
          placeHolder={t('enter_family_name')}
          value={values.familyName}
          onChange={handleChange}
        />

        {!isSSO && (
          <TextInput
            label={t('phone_number')}
            name="phoneNumber"
            placeHolder={t('enter_phone_number')}
            value={values.phoneNumber}
            onChange={handleChange}
            preChildren={
              <Select
                className="!p-0 !gap-1 !h-auto !rounded-none !pr-2 border-r-white border-y-0 border-l-0 bg-dark-subdued-background"
                value={values.countryCode}
                onValueChange={(value) => {
                  handleChange({ target: { name: 'countryCode', value } });
                }}
                options={COUNTRY_CODES.map((code) => ({
                  label: (
                    <div className="flex gap-2 items-center">
                      <span className="text-lg">{code.logo}</span>
                      <span className="text-gray-400 text-sm">{code.value}</span>
                    </div>
                  ),
                  value: code.value,
                }))}
              />
            }
          />
        )}
      </div>
      {error && <span className="text-red-500 text-sm">{error}</span>}

      <ButtonV2
        variant="primary"
        disabled={updateUserMutation.isPending || Object.keys(errors).length > 0}
        style={{ width: '100%' }}
        onClick={() => submitForm()}
      >
        {'Continue'}
      </ButtonV2>
    </div>
  );
}
